import type { ReactNode } from 'react'

/**
 * Configuration for a generic select filter option
 */
export interface DataTableSelectFilterOption<TValue = string> {
  /** The value of the option */
  value: TValue
  /** The label to display */
  label: string
  /** Optional icon to display in the option */
  icon?: ReactNode
}

/**
 * Configuration for a select filter
 */
export interface DataTableSelectFilter<TValue = string> {
  /** Filter type identifier */
  type?: 'select'
  /** Unique identifier for the filter */
  id: string
  /** Label to display above the filter */
  label: string
  /** Placeholder text for the select */
  placeholder: string
  /** Current selected value (use 'all' for no filter) */
  value: TValue | 'all'
  /** Default value for this filter. Used to determine if a filter was changed from its initial state. Defaults to 'all'. */
  defaultValue?: TValue | 'all'
  /** Available options */
  options: DataTableSelectFilterOption<TValue>[]
  /** Optional async fetcher for searchable options (backend search). */
  fetchOptions?: (query: string) => Promise<DataTableSelectFilterOption<TValue>[]>
  /** Enables searchable combobox behavior for this select filter. */
  searchable?: boolean
  /** Placeholder shown in the combobox search input. */
  searchPlaceholder?: string
  /** Message shown when no options match the search query. */
  emptyMessage?: string
  /** Whether to show the "all" option in the select. Defaults to true. */
  showAllOption?: boolean
  /** Callback when value changes */
  onValueChange: (value: TValue | 'all') => void
  /** Callback when value changes in pending state (drawer/sheet) before applying filters */
  onPendingValueChange?: (value: TValue | 'all') => void
  /** Optional values that should be applied immediately on mobile drawer, closing it first */
  mobileImmediateValues?: string[]
  /** Optional values that should be applied immediately without closing drawer/sheet */
  immediateValues?: string[]
  /** Optional width class (default: w-full lg:w-[180px]) */
  width?: string
}

/**
 * Configuration for a date input filter
 */
export interface DataTableDateFilter {
  /** Filter type identifier */
  type: 'date'
  /** Unique identifier for the filter */
  id: string
  /** Label used in mobile drawer */
  label: string
  /** Current value in YYYY-MM-DD format */
  value: string
  /** Default value used to calculate active filter count */
  defaultValue?: string
  /** Callback when value changes */
  onValueChange: (value: string) => void
  /** Optional placeholder (browser-dependent for date inputs) */
  placeholder?: string
  /** Optional minimum date */
  min?: string
  /** Optional maximum date */
  max?: string
  /** Optional width class */
  width?: string
}

export type DataTableFilter = DataTableSelectFilter | DataTableDateFilter

/**
 * Configuration for the search input
 */
export interface DataTableSearchConfig {
  /** Current search value */
  value: string
  /** Placeholder text */
  placeholder: string
  /** Callback when search value changes */
  onChange: (value: string) => void
}

/**
 * Props for DataTableFilters component (desktop)
 */
export interface DataTableFiltersProps {
  /** Optional className for root container */
  className?: string
  /** Optional search configuration */
  search?: DataTableSearchConfig
  /** Array of filters (select/date) */
  filters?: DataTableFilter[]
  /** Callback to clear all filters. When provided, shows a clear button when filters are active. */
  onClearFilters?: () => void
}

/**
 * Props for DataTableFilterDrawer component (mobile)
 */
export interface DataTableFilterDrawerProps {
  /** Array of filters (select/date) */
  filters: DataTableFilter[]
  /** Callback to clear all filters */
  onClearFilters: () => void
  /** Optional drawer title */
  title?: string
  /** Optional drawer description */
  description?: string
  /** Custom trigger element. If not provided, no trigger is rendered (use controlled mode). */
  trigger?: ReactNode
}

/**
 * Calculate the number of filters that differ from their default value.
 */
export function getActiveFilterCount(filters: DataTableFilter[]): number {
  return filters.filter((f) => {
    if (f.type === 'date') {
      const defaultVal = f.defaultValue ?? ''
      return f.value !== defaultVal
    }

    const defaultVal = f.defaultValue ?? 'all'
    return String(f.value) !== String(defaultVal)
  }).length
}
